/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Export Button Component
 * Feature #117: Operational Dashboards
 * Constitution: v1.12.0
 */

import React, { useState } from 'react';
import { Download } from 'lucide-react';

export interface ExportButtonProps {
  data: any[] | string;
  filename: string;
  columns?: { key: string; label: string }[];
  disabled?: boolean;
  className?: string;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  filename,
  columns,
  disabled = false,
  className = '',
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const convertToCSV = () => {
    // If data is already a CSV string, return it directly
    if (typeof data === 'string') {
      return data;
    }

    if (!data || data.length === 0 || !columns) return '';

    // CSV header
    const headers = columns.map((col) => col.label).join(',');
    
    // CSV rows
    const rows = data.map((row) => {
      return columns.map((col) => {
        const value = row[col.key];
        // Handle null/undefined
        if (value === null || value === undefined) return '';
        // Escape quotes and wrap in quotes if contains comma
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',');
    });

    return [headers, ...rows].join('\n');
  };

  const isEmpty = typeof data === 'string' ? data.length === 0 : !data || data.length === 0;

  const handleExport = async () => {
    if (isExporting || disabled || isEmpty) return;

    setIsExporting(true);
    try {
      const csv = convertToCSV();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={disabled || isExporting || isEmpty}
      className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ${className}`}
    >
      <Download className="h-4 w-4" />
      {isExporting ? 'Exporting...' : 'Export CSV'}
    </button>
  );
};
